import api from '../../services/api';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// Async thunk for getting all exam schedules
export const getExamSchedulesList = createAsyncThunk(
  'examSchedules/list',
  async () => {
    try {
      const response = await api({
        baseURL: process.env.REACT_APP_API_BASE_URL,
        url: '/schedule/',
        method: 'GET',
      });
      return response?.data;
    } catch (error) {
      console.log('Error fetching exam schedules:', error);
      throw error;
    }
  },
);

export const fetchPassPercentage = createAsyncThunk(
  'passPercentagevalue/list',
  async (percentage, { rejectWithValue }) => {
    try {
      const response = await api({
        baseURL: process.env.REACT_APP_API_BASE_URL,
        url: 'grade/passPercentage/',
        method: 'POST',
        headers: { percentage },
      });
      return response?.data;
    } catch (error) {
      console.log('Error fetching Pass Percentage:', error);
      rejectWithValue(error.response.data);
    }
  },
);
// Async thunk for creating a new exam schedule
export const createExamSchedule = createAsyncThunk(
  'examSchedules/create',
  async (scheduleData, { rejectWithValue }) => {
    try {
      const payload = {
        date: scheduleData.date,
        marks: scheduleData.marks,
        passMarks: scheduleData.passMarks,
        subjectName: scheduleData.subjectName,
        examType: scheduleData.examType,
        departmentName: scheduleData.departmentName,
        classesName: scheduleData.classesName,
        sectionName: scheduleData.sectionName || null,
        startTime: scheduleData.startTime || null,
        endTime: scheduleData.endTime || null,
      };
      const response = await api({
        baseURL: process.env.REACT_APP_API_BASE_URL,
        url: '/schedule/',
        method: 'POST',
        data: payload,
      });
      return response?.data;
    } catch (error) {
      console.log('Error creating exam schedule:', error);
      return rejectWithValue(error.response.data);
    }
  },
);

// Async thunk for updating an exam schedule
export const updateExamSchedule = createAsyncThunk(
  'examSchedules/update',
  async (scheduleData) => {
    try {
      const response = await api({
        baseURL: process.env.REACT_APP_API_BASE_URL,
        url: `/schedule/`,
        method: 'PUT',
        data: scheduleData,
      });
      return {
        id: scheduleData.id,
        data: response?.data,
      };
    } catch (error) {
      console.log('Error updating exam schedule:', error);
      throw error;
    }
  },
);

// Async thunk for deleting an exam schedule
export const deleteExamSchedule = createAsyncThunk(
  'examSchedules/delete',
  async (id, { rejectWithValue }) => {
    try {
      await api({
        baseURL: process.env.REACT_APP_API_BASE_URL,
        url: `/schedule/${id}/`,
        method: 'DELETE',
      });
      return { id };
    } catch (error) {
      console.log('Error deleting exam schedule:', error);
      return rejectWithValue(error.response.data);
    }
  },
);

const examScheduleSlice = createSlice({
  name: 'examSchedules',
  initialState: {
    examSchedulesList: [],
    status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    error: null,
  },
  reducers: {
    // Clear error message
    clearExamScheduleError: (state) => {
      state.error = null;
    },
    // Reset status to idle
    resetExamScheduleStatus: (state) => {
      state.status = 'idle';
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get exam schedules list cases
      .addCase(getExamSchedulesList.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(getExamSchedulesList.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.examSchedulesList = action.payload || [];
        state.error = null;
      })
      .addCase(getExamSchedulesList.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })

      // Create exam schedule cases
      .addCase(createExamSchedule.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(createExamSchedule.fulfilled, (state, action) => {
        state.status = 'succeeded';
        // Add the new exam schedule to the list if it's provided
        if (action.payload) {
          state.examSchedulesList.push(action.payload);
        }
        state.error = null;
      })
      .addCase(createExamSchedule.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })

      // Update exam schedule cases
      .addCase(updateExamSchedule.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(updateExamSchedule.fulfilled, (state, action) => {
        state.status = 'succeeded';
        // Update the exam schedule in the list
        const { id, data } = action.payload;
        const existingScheduleIndex = state.examSchedulesList.findIndex(
          (schedule) => schedule.id === id,
        );
        if (existingScheduleIndex !== -1 && data) {
          state.examSchedulesList[existingScheduleIndex] = data;
        }
        state.error = null;
      })
      .addCase(updateExamSchedule.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })

      // Delete exam schedule cases
      .addCase(deleteExamSchedule.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(deleteExamSchedule.fulfilled, (state, action) => {
        state.status = 'succeeded';
        // Remove the exam schedule from the list
        const { id } = action.payload;
        state.examSchedulesList = state.examSchedulesList.filter(
          (schedule) => schedule.id !== id,
        );
        state.error = null;
      })
      .addCase(deleteExamSchedule.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      });
  },
});

// Export actions
export const { clearExamScheduleError, resetExamScheduleStatus } =
  examScheduleSlice.actions;

// Export reducer
export default examScheduleSlice.reducer;
